import {
  AdminSubscriptionDowngradeState,
  adminSubscriptionDowngradeStateSchema,
  AvailableCountriesResponse,
  availableCountriesResponseSchema,
  EnabledJurisdictionsResponse,
  enabledJurisdictionsResponseSchema,
  OrganizationAdminUpdate,
  OrganizationDetails,
  organizationDetailsSchema,
  OrganizationDetailsUpdate,
  OrganizationSettings,
  organizationSettingsSchema,
  OrganizationSettingUpdate,
  PackagingCapabilitiesResponse,
  packagingCapabilitiesResponseSchema,
  PackagingCapability,
} from '@/schemas';
import { Organization, OrganizationUser } from '@/types/organizations';
import { PaginationApiResponse } from '@/types/shared';
import api, { API_PREFIX } from './api';
import { ADMIN_API_REQUEST_TIMEOUT_MS } from './request-timeouts';

export const ORGANIZATIONS_STATE_KEY = 'organizations';

export const getOrganizations = (params: Record<string, any>) => {
  return api.get<PaginationApiResponse<Organization>>(`${API_PREFIX}/organizations/search`, {
    params,
  });
};

export const getOrgDetails = async (orgId: string, reveal?: string) => {
  const params: { reveal?: string } = {};
  if (reveal) {
    params.reveal = reveal;
  }

  const response = await api.get<OrganizationDetails>(`${API_PREFIX}/organization_details`, {
    headers: {
      'x-organization-id': orgId,
    },
    params,
    timeout: ADMIN_API_REQUEST_TIMEOUT_MS,
  });

  // Validate the API response with Zod schema
  try {
    const validatedData = organizationDetailsSchema.parse(response.data);
    return { ...response, data: validatedData };
  } catch (error) {
    console.error('Get organization details API validation failed:', error);
    // Return the raw response as fallback, but log the validation error
    return response;
  }
};

export const getOrganizationSettings = async (orgId: string) => {
  const response = await api.get<OrganizationSettings>(`${API_PREFIX}/organization-settings/${orgId}`, {
    params: {
      organization_id: orgId,
    },
  });

  // Validate the API response with Zod schema
  try {
    const validatedData = organizationSettingsSchema.parse(response.data);
    return { ...response, data: validatedData };
  } catch (error) {
    console.error('Get organization settings API validation failed:', error);
    // Return the raw response as fallback, but log the validation error
    return response;
  }
};

export const getEnabledJurisdictions = async (orgId: string) => {
  const response = await api.get<EnabledJurisdictionsResponse>(
    `${API_PREFIX}/organization-settings/${orgId}/enabled-jurisdictions`,
    {
      params: {
        organization_id: orgId,
      },
    }
  );
  const data: EnabledJurisdictionsResponse = response.data;

  // Validate the API response with Zod schema
  try {
    const validatedData = enabledJurisdictionsResponseSchema.parse(data);
    return { ...response, data: validatedData };
  } catch (error) {
    console.error('Enabled jurisdictions API validation failed:', error);
    // Return the raw response as fallback, but log the validation error
    return response;
  }
};

export const addEnabledJurisdiction = async (orgId: string, code: string) => {
  return api.put<EnabledJurisdictionsResponse>(
    `${API_PREFIX}/organization-settings/${orgId}/enabled-jurisdictions/${code}`,
    null,
    {
      params: {
        organization_id: orgId,
      },
    }
  );
};

export const removeEnabledJurisdiction = async (orgId: string, code: string) => {
  return api.delete<EnabledJurisdictionsResponse>(
    `${API_PREFIX}/organization-settings/${orgId}/enabled-jurisdictions/${code}`,
    {
      params: {
        organization_id: orgId,
      },
    }
  );
};

export const getPackagingCapabilities = async (orgId: string) => {
  const response = await api.get<PackagingCapabilitiesResponse>(
    `${API_PREFIX}/organization-settings/${orgId}/packaging`,
    {
      params: {
        organization_id: orgId,
      },
    }
  );
  // A payload that fails validation is a load error, not data. The switches
  // read editable and can_enable from it, so an unchecked body could show a
  // partner-locked capability as a toggle.
  const validatedData = packagingCapabilitiesResponseSchema.parse(response.data);
  return { ...response, data: validatedData };
};

export const updatePackagingCapability = (orgId: string, capability: PackagingCapability, enabled: boolean) => {
  return api.patch<PackagingCapabilitiesResponse>(
    `${API_PREFIX}/organization-settings/${orgId}/packaging/${capability}`,
    { enabled },
    {
      params: {
        organization_id: orgId,
      },
    }
  );
};

export const updateOrganizationSettings = (orgId: string, payload: OrganizationSettingUpdate) => {
  return api.put<OrganizationSettings>(`${API_PREFIX}/organization-settings/${orgId}`, payload, {
    params: {
      organization_id: orgId,
    },
  });
};

export const updateOrganizationDetails = (orgId: string, payload: OrganizationDetailsUpdate) => {
  return api.put<OrganizationDetails>(`${API_PREFIX}/organization_details`, payload, {
    headers: {
      'x-organization-id': orgId,
    },
  });
};

/** A null filer_id means filings are assigned as normal. */
export interface SpecialHandlingPayload {
  reason: string;
  filer_id: string | null;
}

const SPECIAL_HANDLING_PATH = `${API_PREFIX}/organization_details/special_handling`;

export const turnOnSpecialHandling = (orgId: string, payload: SpecialHandlingPayload) => {
  return api.post(SPECIAL_HANDLING_PATH, payload, { headers: { 'x-organization-id': orgId } });
};

export const updateSpecialHandling = (orgId: string, payload: SpecialHandlingPayload) => {
  return api.patch(SPECIAL_HANDLING_PATH, payload, { headers: { 'x-organization-id': orgId } });
};

export const turnOffSpecialHandling = (orgId: string) => {
  return api.delete(SPECIAL_HANDLING_PATH, { headers: { 'x-organization-id': orgId } });
};

export const updateOrganizationAdminFields = (orgId: string, payload: OrganizationAdminUpdate) => {
  return api.put<Organization>(`${API_PREFIX}/organizations/${orgId}`, payload);
};

/**
 * Admin subscription downgrade facts for the Downgrade control.
 * Returns null on 204 (org has no Chargebee subscription id).
 */
export const getAdminDowngradeSubscription = async (orgId: string): Promise<AdminSubscriptionDowngradeState | null> => {
  const response = await api.get<AdminSubscriptionDowngradeState>(
    `${API_PREFIX}/organizations/${orgId}/downgrade/subscription`
  );
  if (response.status === 204) {
    return null;
  }
  return adminSubscriptionDowngradeStateSchema.parse(response.data);
};

export const createPhysicalMailOnboardingLink = (orgId: string, customerEmail?: string) => {
  const url = `${API_PREFIX}/organizations/${orgId}/physical-mail/onboard`;
  if (customerEmail) {
    return api.get<{ url: string | null; completed: boolean }>(url, {
      params: { customer_email: customerEmail },
    });
  }
  return api.get<{ url: string | null; completed: boolean }>(url);
};

export type PhysicalMailOnboardingRecipientResult = {
  email: string;
  url: string | null;
  completed: boolean;
  emailed: boolean;
  error: string | null;
};

export type PhysicalMailOnboardingBatchResponse = {
  results: PhysicalMailOnboardingRecipientResult[];
};

/** KA-2031: request Stable activation reminders for one or more org users. */
export const createPhysicalMailOnboardingBatch = (orgId: string, customerEmails: string[]) => {
  return api.post<PhysicalMailOnboardingBatchResponse>(`${API_PREFIX}/organizations/${orgId}/physical-mail/onboard`, {
    customer_emails: customerEmails,
  });
};

export const getOrganizationUsers = (orgId: string) => {
  // API_PREFIX already ends in /admin; the backend route is
  // GET /v1/admin/organizations/{id}/users, so do not prefix a second /admin.
  return api.get<{ users: OrganizationUser[]; total_users: number | null }>(
    `${API_PREFIX}/organizations/${orgId}/users`,
    { params: { page_size: 100, page_number: 0, role: null } }
  );
};

export const AVAILABLE_COUNTRIES_STATE_KEY = 'available_countries';

// Unlike other API functions in this file that catch validation errors and return
// fallback data, this intentionally lets Zod failures propagate. When the query
// errors, the page utilities fall back to FALLBACK_COUNTRY_ORDER, providing a
// fully functional experience without silently masking bad data.
export const getAvailableCountries = async () => {
  const response = await api.get<AvailableCountriesResponse>(`${API_PREFIX}/jurisdictions/countries`);
  const validatedData = availableCountriesResponseSchema.parse(response.data);
  return { ...response, data: validatedData };
};
